"use client";

import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { scrollToElement } from "@/service/scroll/scroll";

const MAX_HEIGHT = 500;

export const ExpandableListWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const uuid = useId();

  useLayoutEffect(() => {
    if (contentRef.current) {
      const el = contentRef.current;
      setIsTruncated(el.scrollHeight > MAX_HEIGHT);
    }
  }, [children]);

  const handleCollapse = () => {
    setIsExpanded(false);
    scrollToElement({
      selector: `#expandable-list-wrapper-${CSS.escape(uuid)}`,
      behavior: "instant",
    });
  };

  return (
    <div
      className={cn("relative", isExpanded && "pb-4")}
      id={`expandable-list-wrapper-${uuid}`}
    >
      <div
        ref={contentRef}
        className={cn(
          "transition-all duration-300",
          !isExpanded && isTruncated && "max-h-[600px] overflow-hidden",
        )}
        style={isExpanded ? { maxHeight: "none" } : undefined}
      >
        {children}
      </div>

      {isTruncated && !isExpanded && (
        <div className="absolute right-0 bottom-0 left-0 flex h-32 items-end justify-center bg-gradient-to-t from-white from-40% to-transparent pb-4">
          <Button
            onClick={() => setIsExpanded(true)}
            variant={"outline"}
            className={"mx-4 w-full"}
            size={"sm"}
          >
            Rozwiń
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      )}

      {isTruncated && isExpanded && (
        <div className="mt-4 flex justify-center">
          <Button
            variant={"outline"}
            onClick={handleCollapse}
            className={"mx-4 w-full"}
            size={"sm"}
          >
            Pokaż mniej
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
